'use client';

import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

import BgImage from '@/assets/images/pages-bg.jpg';
import { BaseDropdown } from '@/components/forms/base-dropdown/BaseDropdown.component';
import { PaperBox } from '@/components/paper-box/PaperBox.component';
import { PodContent } from '@/components/pod-content/PodContent.component';
import { GTMEvent } from '@/features/google-tag-manager/components/GoogleTagManager';
import { GetPrivacyPolicy } from '@/features/pages/api/privacy-policy';

import { BodyContentType } from '../../types';

import styles from './PrivacyPolicyContent.module.scss';

interface PrivacyPolicy {
    id: string;
    // documentId is what v5's findOne accepts in the URL path. The list
    // response surfaces it alongside `id`; the detail fetch prefers it
    // post-cutover. While Strapi is still v4, documentId is undefined and
    // we fall back to the numeric id (which v4's findOne accepts).
    documentId?: string;
    title: string;
    description: string;
    published_date: string;
    body: BodyContentType[] | BodyContentType;
}

// Pick the URL path segment for the detail-view fetch:
//   - v5 Strapi: documentId (numeric id is rejected)
//   - v4 Strapi: numeric id (documentId is absent)
// Cast both to string so the dropdown value type stays consistent.
const detailLookup = (policy: PrivacyPolicy) => String(policy.documentId ?? policy.id);

export const PrivacyPolicyContent = ({ lang }: { lang: string }) => {
    const [selectedDocumentId, setSelectedDocumentId] = useState<string>('');
    const [privacyPolicies, setPrivacyPolicies] = useState<PrivacyPolicy[]>([]);
    const [selectedPolicy, setSelectedPolicy] = useState<PrivacyPolicy | null>(null);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const router = useRouter();
    useEffect(() => {
        const fetchListData = async () => {
            const list = await GetPrivacyPolicy({
                language: lang,
            });

            if (list.length === 0) {
                router.push('/not-found');
            }

            setPrivacyPolicies(list);
            const firstLookup = detailLookup(list[0]);
            setSelectedDocumentId(firstLookup);

            // Fetch the full content for the first policy on initial load
            const firstPolicy = await GetPrivacyPolicy({
                language: lang,
                documentId: firstLookup,
            });

            if (firstPolicy) {
                setSelectedPolicy(firstPolicy);
            }

            setIsInitialLoad(false);
        };
        fetchListData();
    }, [lang, router]);

    useEffect(() => {
        // Skip if it's the initial load (already fetched in first useEffect)
        if (isInitialLoad || !selectedDocumentId) return;

        const fetchPolicyData = async () => {
            const policy = await GetPrivacyPolicy({
                language: lang,
                documentId: selectedDocumentId,
            });

            if (policy) {
                setSelectedPolicy(policy);
            }
        };

        fetchPolicyData();
    }, [selectedDocumentId, lang, isInitialLoad]);

    const handleIdChange = (documentId: string) => {
        if (documentId === selectedDocumentId) return;
        setSelectedDocumentId(documentId);
    };

    const dropdownOptions = privacyPolicies.map((policy) => ({
        label: new Date(policy.published_date).toLocaleDateString(),
        value: detailLookup(policy),
    }));

    return (
        <>
            <section className={styles.wrapper}>
                <div className={styles.paperBox}>
                    <div className={styles.dateSelector}>
                        <BaseDropdown
                            value={selectedDocumentId}
                            options={dropdownOptions}
                            onChange={handleIdChange}
                            placeholder=""
                            label="Privacy Policy Version"
                        />
                    </div>
                    <PaperBox
                        title={selectedPolicy?.title}
                        tapeBoxClassName={styles.paperBoxTape}
                        contentClassName={styles.paperBoxContent}
                        vertical
                    >
                        <h4 className={styles.description}>{selectedPolicy?.description || ''}</h4>
                        {selectedPolicy?.body ? (
                            <PodContent content={selectedPolicy.body} />
                        ) : (
                            <></>
                        )}
                    </PaperBox>
                </div>
                <Image className={styles.bgImage} src={BgImage} alt="" fill />
            </section>
            <GTMEvent pageTitle="privacy-page" />
        </>
    );
};
